import type { FC, ReactNode } from 'react'
import type { Invoice } from '@/types'

import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import {
    CircleCheckBig,
    Hourglass,
    Wallet,
    ReceiptText,
    Search,
    X
} from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { DataGate } from '@/components/app/data-gate'
import { PageContainer, PageHeader } from '@/components/app/page-header'
import { Button } from '@/components/kit/button'
import { Input, Select } from '@/components/kit/field'
import { Segmented } from '@/components/kit/segmented'
import { EmptyState } from '@/components/kit/empty-state'
import { Card, SectionHeader } from '@/components/kit/section'
import { InvoiceRow } from '@/components/billing/invoice-row'
import { MethodSplit } from '@/components/billing/method-split'
import { useJobActions } from '@/components/jobs/job-actions'
import { useNow } from '@/hooks/use-now'
import { useCarWashData } from '@/hooks/use-carwash'
import { dayKey, formatDayLabel, formatMoney } from '@/lib/carwash/format'
import { getTodayStats } from '@/lib/carwash/selectors'
import { normalizePlate } from '@/lib/carwash/plate'
import { DATE_PRESETS, inDateRange, resolveDateFilter } from '@/lib/carwash/ranges'

type BillingTab = 'pending' | 'paid' | 'all'

const PAGE_SIZE = 40

const SummaryCard: FC<{ label: string, value: string, note: string, icon: ReactNode, iconClass: string }> = ({ label, value, note, icon, iconClass }): ReactNode => {
    return (
        <Card className='p-3.5 lg:p-4'>
            <div className='flex items-start justify-between gap-2'>
                <span className='text-[13px] leading-tight font-semibold text-muted-foreground'>
                    {label}
                </span>

                <span className={`grid size-8 shrink-0 place-items-center rounded-lg [&_svg]:size-[18px] ${iconClass}`}>
                    {icon}
                </span>
            </div>

            <p className='mt-1.5 truncate font-heading text-[26px] leading-tight font-extrabold text-foreground'>
                {value}
            </p>

            <p className='mt-1 truncate text-xs font-medium text-muted-foreground'>
                {note}
            </p>
        </Card>
    )
}

const BillingView: FC = (): ReactNode => {
    const router = useRouter()
    const { state } = useCarWashData()
    const jobActions = useJobActions()
    const now = useNow(60_000)

    const [tab, setTab] = useState<BillingTab>('pending')
    const [date, setDate] = useState('today')
    const [query, setQuery] = useState('')
    const [limit, setLimit] = useState(PAGE_SIZE)
    const [initialized, setInitialized] = useState(false)

    useEffect(() => {
        if (!router.isReady || initialized) return

        const requested = router.query.tab
        if (requested === 'pending' || requested === 'paid' || requested === 'all') setTab(requested)
        else if (!state.invoices.some(invoice => invoice.status === 'pending')) setTab('paid')

        setInitialized(true)
    }, [router.isReady, router.query.tab, initialized, state.invoices])

    useEffect(() => {
        setLimit(PAGE_SIZE)
    }, [tab, date, query])

    const currency = state.settings.currency
    const today = useMemo(() => getTodayStats(state, now), [state, now])
    const range = resolveDateFilter(date, now)
    const plateNeedle = normalizePlate(query)
    const textNeedle = query.trim().toLowerCase()

    const filtered = useMemo(() => state.invoices.filter(invoice => {
        if (tab === 'pending' && invoice.status !== 'pending') return false
        if (tab === 'paid' && invoice.status !== 'paid') return false
        if (tab !== 'pending' && !inDateRange(invoice.issuedAt, range)) return false

        if (textNeedle) {
            const plateHit = plateNeedle.length > 0 && invoice.plate.includes(plateNeedle)
            const numberHit = invoice.number.toLowerCase().includes(textNeedle)
            if (!plateHit && !numberHit) return false
        }

        return true
    }).sort((a, b) => b.issuedAt.localeCompare(a.issuedAt)), [state.invoices, tab, range?.start, range?.end, textNeedle, plateNeedle]) // eslint-disable-line react-hooks/exhaustive-deps

    const counts = useMemo(() => ({
        pending: state.invoices.filter(invoice => invoice.status === 'pending').length,
        paid: state.invoices.filter(invoice => invoice.status === 'paid' && inDateRange(invoice.issuedAt, range)).length,
        all: state.invoices.filter(invoice => inDateRange(invoice.issuedAt, range)).length
    }), [state.invoices, range?.start, range?.end]) // eslint-disable-line react-hooks/exhaustive-deps

    const shown = filtered.slice(0, limit)
    const total = filtered.reduce((sum, invoice) => sum + invoice.total, 0)

    const groups = useMemo(() => {
        const map = new Map<string, Invoice[]>()

        for (const invoice of shown) {
            const key = dayKey(invoice.issuedAt)
            const list = map.get(key)
            if (list) list.push(invoice)
            else map.set(key, [invoice])
        }

        return Array.from(map.entries())
    }, [shown])

    return (
        <>
            <PageHeader title='Billing' subtitle={`Today: ${formatMoney(today.sales, currency)} billed`}>
                <div className='relative'>
                    <label htmlFor='bill-search' className='sr-only'>
                        Search bills by vehicle number or invoice number
                    </label>

                    <Input
                        id='bill-search'
                        type='search'
                        autoComplete='off'
                        autoCapitalize='characters'
                        enterKeyHint='search'
                        placeholder='Search vehicle or invoice number'
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        leading={<Search className='size-5' aria-hidden='true' />}
                        trailing={query
                            ? (
                                <button
                                    type='button'
                                    onClick={() => setQuery('')}
                                    aria-label='Clear search'
                                    className='grid size-9 place-items-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700'
                                >
                                    <X className='size-4' aria-hidden='true' />
                                </button>
                            )
                            : undefined}
                        className='h-11 bg-white text-[15px]'
                    />
                </div>
            </PageHeader>

            <PageContainer className='space-y-5'>
                <div className='grid grid-cols-2 gap-3 md:grid-cols-4'>
                    <SummaryCard
                        label="Today's sales"
                        value={formatMoney(today.sales, currency)}
                        note={`${formatMoney(today.collected, currency)} collected`}
                        icon={<Wallet />}
                        iconClass='bg-accent text-accent-foreground'
                    />

                    <SummaryCard
                        label='Paid bills'
                        value={String(today.paidToday)}
                        note={`of ${today.billsToday} bills today`}
                        icon={<ReceiptText />}
                        iconClass='bg-emerald-50 text-emerald-700'
                    />

                    <SummaryCard
                        label='Pending payments'
                        value={formatMoney(today.pendingAmount, currency)}
                        note={`${today.pendingCount} ${today.pendingCount === 1 ? 'bill' : 'bills'} unpaid`}
                        icon={<Hourglass />}
                        iconClass='bg-orange-50 text-orange-700'
                    />

                    <SummaryCard
                        label='Completed vehicles'
                        value={String(today.completedToday)}
                        note='Finished today'
                        icon={<CircleCheckBig />}
                        iconClass='bg-sky-50 text-sky-700'
                    />
                </div>

                <Card className='p-4'>
                    <SectionHeader title='Collected today' description='By payment method' className='mb-3' />
                    <MethodSplit amounts={today.collectedByMethod} currency={currency} />
                </Card>

                <section aria-labelledby='bills-title' className='space-y-3'>
                    <SectionHeader
                        id='bills-title'
                        title='Bills'
                        description={filtered.length ? `${filtered.length} ${filtered.length === 1 ? 'bill' : 'bills'}, ${formatMoney(total, currency)}` : undefined}
                        action={tab !== 'pending' && (
                            <div className='w-40'>
                                <label htmlFor='bill-date' className='sr-only'>
                                    Date range
                                </label>

                                <Select id='bill-date' value={date} onChange={event => setDate(event.target.value)} className='h-10 text-sm'>
                                    {DATE_PRESETS.map(preset => (
                                        <option key={preset.value} value={preset.value}>
                                            {preset.label}
                                        </option>
                                    ))}
                                </Select>
                            </div>
                        )}
                    />

                    <Segmented
                        label='Bill status'
                        value={tab}
                        onChange={setTab}
                        options={[
                            { value: 'pending', label: 'Pending', count: counts.pending },
                            { value: 'paid', label: 'Paid', count: counts.paid },
                            { value: 'all', label: 'All', count: counts.all }
                        ]}
                    />

                    {groups.length === 0
                        ? (
                            <EmptyState
                                icon={<ReceiptText />}
                                title={tab === 'pending' ? 'No unpaid bills' : 'No bills here'}
                                description={tab === 'pending'
                                    ? 'Every completed wash has been paid for.'
                                    : 'Bills are created automatically when a wash is completed.'}
                            />
                        )
                        : groups.map(([key, invoices]) => (
                            <div key={key}>
                                <h3 className='mb-2 flex items-baseline justify-between gap-3 px-1'>
                                    <span className='text-[13px] font-bold tracking-wide text-slate-600 uppercase'>
                                        {formatDayLabel(invoices[0].issuedAt, now)}
                                    </span>

                                    <span className='text-xs font-medium text-muted-foreground tabular-nums'>
                                        {formatMoney(invoices.reduce((sum, invoice) => sum + invoice.total, 0), currency)}
                                    </span>
                                </h3>

                                <div className='grid gap-2 lg:grid-cols-2'>
                                    {invoices.map(invoice => (
                                        <InvoiceRow key={invoice.id} invoice={invoice} currency={currency} onCollect={jobActions.openPayment} />
                                    ))}
                                </div>
                            </div>
                        ))}

                    {filtered.length > shown.length && (
                        <Button variant='secondary' size='lg' className='w-full' onClick={() => setLimit(value => value + PAGE_SIZE)}>
                            {`Show more (${filtered.length - shown.length} left)`}
                        </Button>
                    )}
                </section>
            </PageContainer>
        </>
    )
}

const BillingPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo title='Billing' description="Today's sales, unpaid bills and every invoice, with payment by cash, UPI or card." />

            <DataGate>
                <BillingView />
            </DataGate>
        </Layout>
    )
}

export default BillingPage
